import React, { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button } from '@americanexpress/dls-react';
import { useSelector } from 'react-redux';
import styles from '../SearchWidget/SearchWidget.scss';
import themeStyles from '../themeOverrides.scss';
import { TAB_TO_ELEMENT, TAB_TO_ICON } from '../../constants/linesOfBusinessConstants';
import getTabSelected from '../../selectors/tabSelector';

/* istanbul ignore next -- coverage in __tests__/playwright/navigation.spec.ts */
const SearchWidgetNavigation = ({
  displayedLOBs,
  isOverflowing,
  setIsOverflowing,
  onTabSelected,
}) => {
  const { formatMessage } = useIntl();
  const navRef = useRef(null);
  const selectedTab = useSelector(getTabSelected);
  const tabCount = displayedLOBs.length;
  const tabPosition = displayedLOBs.indexOf(
    displayedLOBs.find((lob) => lob === selectedTab)
  );

  const handleDirectionButtonClick = (direction) => {
    const positionOfNextTab = (tabPosition + direction + tabCount) % tabCount;
    const nextTab = displayedLOBs[positionOfNextTab];
    onTabSelected(nextTab);
    navRef?.current?.querySelector(`button[id="${nextTab}-tab"]`)?.focus();
  };

  useEffect(() => {
    const checkIfNavIsScrollable = () => {
      const tabsListElement = navRef.current.querySelector('#navWidthIndicator');
      setIsOverflowing(
        tabsListElement.scrollWidth > tabsListElement.clientWidth
      );
    };
    checkIfNavIsScrollable();
    window.addEventListener('resize', checkIfNavIsScrollable);
    return () => {
      window.removeEventListener('resize', checkIfNavIsScrollable);
    };
  }, [navRef, displayedLOBs, setIsOverflowing]);

  return (
    <nav
      ref={navRef}
      id="search-widget-navigation"
      className={clsx('flex flex-column flex-justify-between no-scroll', styles.inPageNavContainer)}
      // TODO: add translations for aria-label once we have them, this is to give navigation context
      aria-label={formatMessage({ id: 'aria.search_widget' })}
    >
      <div
        id="search-widget-tabs"
        role="tablist"
        className="flex flex-align-items-center flex-justify-between pad-0 width-full"
      >
        {displayedLOBs.map((lob) => {
          const isSelected = selectedTab === lob;
          return (
            <Button
              variant="tertiary"
              key={lob}
              id={`${lob}-tab`}
              role="tab"
              className={clsx(
                themeStyles.themedButton,
                isOverflowing && styles.iconWithNoText,
                'flex flex-column flex-align-items-center pad-1 flex-justify-center text-nowrap flex-item-grow'
              )}
              onFocus={() => onTabSelected(lob)}
              onKeyDownCapture={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); onTabSelected(lob); }
                if (e.key === 'ArrowLeft') { handleDirectionButtonClick(-1); }
                if (e.key === 'ArrowRight') { handleDirectionButtonClick(1); }
              }}
              onClick={() => onTabSelected(lob)}
              aria-selected={isSelected}
              tabIndex={isSelected ? 0 : -1}
              aria-controls={`${lob}-body`}
              aria-label={formatMessage({ id: lob })}
              reactlytics={{ element: TAB_TO_ELEMENT[lob] || lob }}
            >
              {isOverflowing ? (
                <span className={themeStyles.themeIcon}> {TAB_TO_ICON(true)[lob]} </span>
              ) : (
                <span className={clsx('flex flex-row-lg-up flex-column-md-down', themeStyles.themeIcon)}>
                  {TAB_TO_ICON(true)[lob]}
                  <span className={clsx(isSelected ? 'body-bold' : 'body', 'margin-1-l-lg-up')}>
                    {formatMessage({ id: lob })}
                  </span>
                </span>
              )}
              <hr />
            </Button>
          );
        })}
      </div>
      {/* used to measure the minimum width of the text content, and check for overflow */}
      <span
        id="navWidthIndicator"
        aria-hidden={true}
        className={styles.navWidth}
      >
        {displayedLOBs.map((lob) => (
          <span key={lob} className="pad-1-lr text-nowrap body-bold">
            {formatMessage({ id: lob })}
          </span>
        ))}
      </span>
    </nav>
  );
};

SearchWidgetNavigation.propTypes = {
  displayedLOBs: PropTypes.arrayOf(
    PropTypes.string.isRequired
  ).isRequired,
  isOverflowing: PropTypes.bool,
  setIsOverflowing: PropTypes.func,
  onTabSelected: PropTypes.func.isRequired,
};

export default SearchWidgetNavigation;
